import React, { type FC } from 'react';
import { CardShell } from './card-shell';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import type { ToolCardProps } from './types';
import { safeGet, safeArray, formatDate, formatDateRange } from './types';
import { normalizeAvatarUrl } from '../../../lib/avatar-url';

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  marginBottom: 6,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const sectionItemStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--text-secondary)',
  lineHeight: '20px',
  marginBottom: 4,
};

const separatorStyle: React.CSSProperties = {
  marginTop: 6,
  paddingTop: 12,
  borderTop: '1px solid var(--gem-outline-variant)',
};

export const ProfileSummaryCard: FC<ToolCardProps> = ({ result }) => {
  const profile = safeGet<Record<string, unknown>>(result, 'profile');
  if (!profile) return null;

  const name = safeGet<string>(profile, 'name') ?? 'Unknown';
  const title = safeGet<string>(profile, 'title');
  const bio = safeGet<string>(profile, 'bio');
  const about = safeGet<string>(profile, 'about');
  const location = safeGet<string>(profile, 'location');
  const avatarUrl = safeGet<string>(profile, 'avatarUrl');
  const skills = safeGet<string[]>(profile, 'skills') ?? [];
  const openToWork = safeGet<boolean>(profile, 'openToWork');

  const experiences = safeArray<Record<string, unknown>>(result, 'experiences');
  const educations = safeArray<Record<string, unknown>>(result, 'educations');
  const certifications = safeArray<Record<string, unknown>>(result, 'certifications');
  const projects = safeArray<Record<string, unknown>>(result, 'projects');
  const serviceOffers = safeArray<Record<string, unknown>>(result, 'serviceOffers');

  const initials = name
    .split(' ')
    .map((w) => w.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const topSkills = skills.slice(0, 6);

  return (
    <CardShell className="profile-card-wide">
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {avatarUrl ? (
            <img
              src={normalizeAvatarUrl(avatarUrl, 88) ?? avatarUrl}
              alt={name}
              referrerPolicy="no-referrer"
              style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.06)',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </div>
            {(title || location) && (
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--text-tertiary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginTop: 2,
                }}
              >
                {title}
                {title && location && ' · '}
                {location}
              </div>
            )}
          </div>
        </div>

        {/* Open to work badge */}
        {openToWork && (
          <span className="tabbio-menu-badge" style={{ alignSelf: 'flex-start' }}>
            Open to work
          </span>
        )}

        {/* Bio / about */}
        {(bio || about) && (
          <div
            style={{
              fontSize: 14,
              color: 'var(--text-secondary)',
              lineHeight: '21px',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {bio || about}
          </div>
        )}

        {/* Empty state for sparse profiles */}
        {!title && !bio && !about && topSkills.length === 0 && experiences.length === 0 && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              lineHeight: '20px',
            }}
          >
            Your profile is just getting started. Tell me about your work experience, skills, and education to build it out.
          </div>
        )}

        {/* Skills pills */}
        {topSkills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {topSkills.map((skill, i) => (
              <span key={i} className="tabbio-menu-badge">
                {skill}
              </span>
            ))}
            {skills.length > 6 && (
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--text-tertiary)',
                  padding: '3px 4px',
                  lineHeight: '16px',
                }}
              >
                +{skills.length - 6} more
              </span>
            )}
          </div>
        )}

        {/* Experience section */}
        {experiences.length > 0 && (
          <div style={separatorStyle}>
            <div style={sectionHeaderStyle}>
              <MaterialSymbol name="work" size={14} style={{ color: 'var(--text-tertiary)' }} />
              Experience ({experiences.length})
            </div>
            {experiences.slice(0, 4).map((exp, i) => {
              const expTitle = safeGet<string>(exp, 'title') ?? '';
              const company = safeGet<string>(exp, 'company') ?? '';
              const start = safeGet<string>(exp, 'startDate');
              const end = safeGet<string>(exp, 'endDate');
              const current = safeGet<boolean>(exp, 'current');
              const dateStr = formatDateRange(start, end, current);
              return (
                <div key={i} style={sectionItemStyle}>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    {expTitle}
                  </span>
                  {company && <span>: {company}</span>}
                  {dateStr && (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                      {' '}
                      ({dateStr})
                    </span>
                  )}
                </div>
              );
            })}
            {experiences.length > 4 && (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                +{experiences.length - 4} more
              </div>
            )}
          </div>
        )}

        {/* Education section */}
        {educations.length > 0 && (
          <div style={separatorStyle}>
            <div style={sectionHeaderStyle}>
              <MaterialSymbol name="school" size={14} style={{ color: 'var(--text-tertiary)' }} />
              Education ({educations.length})
            </div>
            {educations.map((edu, i) => {
              const degree =
                safeGet<string>(edu, 'degree') ?? safeGet<string>(edu, 'fieldOfStudy') ?? '';
              const school = safeGet<string>(edu, 'school') ?? '';
              const start = safeGet<string>(edu, 'startDate');
              const end = safeGet<string>(edu, 'endDate');
              const dateStr = formatDateRange(start, end);
              return (
                <div key={i} style={sectionItemStyle}>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    {degree}
                  </span>
                  {school && <span>: {school}</span>}
                  {dateStr && (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                      {' '}
                      ({dateStr})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Certifications section */}
        {certifications.length > 0 && (
          <div style={separatorStyle}>
            <div style={sectionHeaderStyle}>
              <MaterialSymbol
                name="workspace_premium"
                size={14}
                style={{ color: 'var(--text-tertiary)' }}
              />
              Certifications ({certifications.length})
            </div>
            {certifications.map((cert, i) => {
              const certName = safeGet<string>(cert, 'name') ?? '';
              const issuer = safeGet<string>(cert, 'issuer') ?? '';
              const issueDate = safeGet<string>(cert, 'issueDate');
              return (
                <div key={i} style={sectionItemStyle}>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    {certName}
                  </span>
                  {issuer && <span>: {issuer}</span>}
                  {issueDate && (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                      {' '}
                      ({formatDate(issueDate)})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Projects */}
        {projects.length > 0 && (
          <div style={separatorStyle}>
            <div style={sectionHeaderStyle}>
              <MaterialSymbol
                name="folder_open"
                size={14}
                style={{ color: 'var(--text-tertiary)' }}
              />
              Projects ({projects.length})
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                lineHeight: '20px',
              }}
            >
              {projects
                .map((p) => safeGet<string>(p, 'title') ?? '')
                .filter(Boolean)
                .join(', ')}
            </div>
          </div>
        )}

        {/* Services */}
        {serviceOffers.length > 0 && (
          <div style={separatorStyle}>
            <div style={sectionHeaderStyle}>
              <MaterialSymbol
                name="handyman"
                size={14}
                style={{ color: 'var(--text-tertiary)' }}
              />
              Services ({serviceOffers.length})
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                lineHeight: '20px',
              }}
            >
              {serviceOffers
                .map((s) => safeGet<string>(s, 'title') ?? '')
                .filter(Boolean)
                .join(', ')}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .profile-card-wide { max-width: 520px !important; }
      `}</style>
    </CardShell>
  );
};
